import { useEffect, useMemo, useState } from "react";
import axios, { API } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getBackendUrl } from "@/lib/backend";
import { formatApiError } from "@/lib/apiError";
import { Loader2, Sparkles, Check, Download, Pencil } from "lucide-react";

const BACKEND = getBackendUrl();
const MAX = 700;

/**
 * DesignBriefReviewDialog
 *
 * Pre-export gate — every item's `design_brief` is reviewed before the
 * Lookbook PDF is packed. Three actions per item:
 *   * Keep as-is (either the existing brief or the raw refined prompt)
 *   * Edit inline
 *   * Generate with AI (free for the first 3 attempts, then 0.5 credit)
 *
 * When the user hits "Save briefs and export", we PATCH any locally
 * edited briefs (draft ≠ server value) and then call `onConfirm()`
 * which kicks off the ZIP download in the parent.
 *
 * Design decisions:
 *   * Local `drafts` state keeps typing snappy — no debounced network
 *     traffic per keystroke.
 *   * `saveIfDirty` on save + generate ensures the server always
 *     matches the visible textarea before AI runs / export starts.
 */
export default function DesignBriefReviewDialog({
  open,
  onOpenChange,
  portfolio,
  onConfirm,
  exporting,
}) {
  const items = useMemo(() => portfolio?.items || [], [portfolio]);
  const [drafts, setDrafts] = useState({});          // { [itemId]: text }
  const [meta, setMeta]     = useState({});          // { [itemId]: {attempts, remaining_free, next_cost, source} }
  const [busy, setBusy]     = useState({});          // { [itemId]: 'ai' | 'save' | null }
  const [editing, setEditing] = useState({});       // { [itemId]: bool }

  // Seed drafts from the portfolio each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    const nextDrafts = {};
    const nextMeta = {};
    for (const it of items) {
      nextDrafts[it.id] = (it.design_brief || it.prompt || "").slice(0, MAX);
      nextMeta[it.id] = {
        attempts: it.design_brief_attempts || 0,
        source: it.design_brief_source || (it.design_brief ? "manual" : "prompt"),
        remaining_free: Math.max(0, 3 - (it.design_brief_attempts || 0)),
      };
    }
    setDrafts(nextDrafts);
    setMeta(nextMeta);
    setEditing({});
    setBusy({});
  }, [open, items]);

  const setDraft = (id, text) => {
    setDrafts((d) => ({ ...d, [id]: text.slice(0, MAX) }));
  };

  const isDirty = (it) => (drafts[it.id] || "") !== (it.design_brief || "");

  const saveOne = async (it) => {
    const text = (drafts[it.id] || "").trim();
    if (!text) {
      toast.error(`${it.design_number || "Piece"} — brief cannot be empty.`);
      return false;
    }
    setBusy((b) => ({ ...b, [it.id]: "save" }));
    try {
      await axios.patch(
        `${API}/studio/portfolios/${portfolio.id}/items/${it.id}/design-brief`,
        { design_brief: text },
      );
      setMeta((m) => ({ ...m, [it.id]: { ...m[it.id], source: "manual" } }));
      return true;
    } catch (err) {
      toast.error(formatApiError(err, `Save failed for ${it.design_number}.`));
      return false;
    } finally {
      setBusy((b) => ({ ...b, [it.id]: null }));
    }
  };

  const generateOne = async (it) => {
    setBusy((b) => ({ ...b, [it.id]: "ai" }));
    try {
      const r = await axios.post(
        `${API}/studio/portfolios/${portfolio.id}/items/${it.id}/design-brief/generate`,
      );
      const brief = r.data?.design_brief || "";
      setDrafts((d) => ({ ...d, [it.id]: brief }));
      setMeta((m) => ({
        ...m,
        [it.id]: {
          attempts: r.data.attempts,
          source: "ai",
          remaining_free: r.data.remaining_free,
          next_cost: r.data.next_cost,
        },
      }));
      setEditing((e) => ({ ...e, [it.id]: false }));
      if (r.data.charged > 0) {
        toast.success(`${it.design_number} · brief regenerated · ${r.data.charged} credit charged.`);
      } else {
        const left = r.data.remaining_free;
        toast.success(
          `${it.design_number} · brief generated · ${left} free regeneration${left === 1 ? "" : "s"} left.`,
        );
      }
    } catch (err) {
      toast.error(formatApiError(err, "AI brief failed."));
    } finally {
      setBusy((b) => ({ ...b, [it.id]: null }));
    }
  };

  const saveAndExport = async () => {
    // Save every dirty draft first so the PDF gets fresh copy.
    for (const it of items) {
      if (isDirty(it)) {
        const ok = await saveOne(it);
        if (!ok) return;
      }
    }
    onConfirm?.();
  };

  if (!portfolio) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="brief-review-dialog"
        className="max-w-3xl bg-[#0B0B0B] border-white/10 text-white max-h-[90vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" /> Review design briefs
          </DialogTitle>
          <p className="text-[11px] text-white/50 mt-1">
            Each brief becomes the "Design brief" paragraph under the hero product in the lookbook PDF.
            Keep as-is, edit inline, or regenerate with AI (first 3 regenerations free per piece, then 0.5 credit).
          </p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 pr-1 -mr-1">
          {items.length === 0 && (
            <div className="text-xs text-white/40 text-center py-8">No pieces in this portfolio.</div>
          )}
          {items.map((it) => {
            const draft = drafts[it.id] || "";
            const m = meta[it.id] || {};
            const isEditing = !!editing[it.id];
            const isBusyAi = busy[it.id] === "ai";
            const isBusySave = busy[it.id] === "save";
            const nextCost = m.next_cost ?? (m.remaining_free > 0 ? 0 : 0.5);
            const briefEmpty = !((it.design_brief || "").trim());

            return (
              <div
                key={it.id}
                data-testid={`brief-item-${it.design_number}`}
                className="rounded-xl border border-white/10 bg-[#111] p-3"
              >
                <div className="flex gap-3">
                  <div className="w-20 h-20 rounded-lg bg-[#080808] overflow-hidden flex-none border border-white/8">
                    {it.image_url && (
                      <img
                        src={`${BACKEND}${it.image_url}`}
                        alt={it.design_number}
                        className="w-full h-full object-cover"
                        draggable={false}
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">{it.design_number}</div>
                        <div className="text-xs text-white/80 truncate">{it.style || it.product_type || "Piece"}</div>
                      </div>
                      <div className="flex items-center gap-1 text-[10px] text-white/45">
                        {m.source === "ai" && <span className="text-[#D4AF37]">AI</span>}
                        {m.source === "manual" && <span>Edited</span>}
                        {m.source === "prompt" && briefEmpty && <span className="text-orange-400">Uses raw prompt</span>}
                      </div>
                    </div>

                    {isEditing ? (
                      <textarea
                        value={draft}
                        onChange={(e) => setDraft(it.id, e.target.value)}
                        rows={4}
                        data-testid={`brief-textarea-${it.design_number}`}
                        placeholder="Describe the concept, mood, and wearer — 2 to 3 sentences."
                        className="mt-2 w-full text-[12px] leading-relaxed bg-[#0A0A0A] border border-white/12 rounded-md p-2 text-white/85 outline-none focus:border-[#D4AF37]/50 resize-y"
                      />
                    ) : (
                      <p
                        className="mt-2 text-[12px] leading-relaxed text-white/75 whitespace-pre-wrap"
                        data-testid={`brief-preview-${it.design_number}`}
                      >
                        {draft || <span className="italic text-white/35">No brief yet — click Edit or Generate.</span>}
                      </p>
                    )}

                    <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px]">
                      {!isEditing ? (
                        <button
                          type="button"
                          onClick={() => setEditing((e) => ({ ...e, [it.id]: true }))}
                          data-testid={`brief-edit-${it.design_number}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/35 transition"
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={async () => {
                            const ok = await saveOne(it);
                            if (ok) setEditing((e) => ({ ...e, [it.id]: false }));
                          }}
                          disabled={isBusySave}
                          data-testid={`brief-save-${it.design_number}`}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition disabled:opacity-40"
                        >
                          {isBusySave ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                          Save edits
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => generateOne(it)}
                        disabled={isBusyAi}
                        data-testid={`brief-ai-${it.design_number}`}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-50"
                      >
                        {isBusyAi ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                        {nextCost > 0 ? `Generate · ${nextCost} credit` : "Generate with AI · Free"}
                      </button>
                      <span className="text-[10px] text-white/40 ml-auto">
                        {draft.length}/{MAX} · {m.remaining_free ?? 3} free regenerations left
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="pt-3 border-t border-white/10 flex items-center justify-between gap-3">
          <div className="text-[11px] text-white/45">
            Pieces without an authored brief will fall back to the refined prompt in the PDF.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              data-testid="brief-cancel"
              className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/70 hover:text-white hover:border-white/30 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={saveAndExport}
              disabled={exporting || items.length === 0}
              data-testid="brief-confirm-export"
              className="inline-flex items-center gap-1.5 text-xs px-4 py-1.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-50"
            >
              {exporting
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Packing lookbook…</>
                : <><Download className="w-3.5 h-3.5" /> Save briefs & export</>}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
